import { useCallback } from 'react'
import { getDatabase, ref, get, set, update } from 'firebase/database'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { useDispatch, useSelector } from 'react-redux'
import { auth } from '../auth/firebase'
import {
  ACCESS_CONTROL_ROOT,
  MAIL_ROUTE_KEYS,
  buildSeedMailRoutes,
  buildSeedPermissions,
  emailToKey,
  emptyReports,
  topicToMailRouteKey,
} from '../helper/accessControlConstants'
import { setPermissionsCache } from '../helper/authorizedUsers'
import { setMailRoutesCache } from '../helper/mailRoutesCache'
import {
  accessControlLoadFail,
  accessControlLoadStart,
  accessControlLoaded,
  setMailRoutesLocal,
  setPermissionLocal,
} from '../features/accessControlSlice'
import { toastErrorNotify, toastSuccessNotify } from '../helper/ToastNotify'

const ROOT = ACCESS_CONTROL_ROOT

const mapPermissionsFromRtdb = (val) => {
  if (!val) return {}
  const out = {}
  Object.values(val).forEach((entry) => {
    const email = String(entry?.email || '').trim().toLowerCase()
    if (!email) return
    out[email] = {
      email,
      reports: { ...emptyReports(), ...(entry.reports || {}) },
      updatedAt: entry.updatedAt || null,
      updatedBy: entry.updatedBy || null,
    }
  })
  return out
}

const normalizeMailRoutes = (val) => {
  const seed = buildSeedMailRoutes()
  const out = {}
  MAIL_ROUTE_KEYS.forEach((key) => {
    const src = val?.[key] || seed[key] || { to: [], cc: [] }
    out[key] = {
      to: Array.isArray(src.to) ? src.to.map((e) => String(e).toLowerCase()) : [],
      cc: Array.isArray(src.cc) ? src.cc.map((e) => String(e).toLowerCase()) : [],
    }
  })
  return out
}

const useAccessControl = () => {
  const dispatch = useDispatch()
  const { currentUser } = useSelector((state) => state.auth)
  const accessState = useSelector((state) => state.accessControl)

  const getDb = () => getDatabase(auth.app)
  const getCallables = () => getFunctions(auth.app)

  const hydrateCaches = useCallback(
    (permissions, mailRoutes) => {
      setPermissionsCache(permissions, true)
      setMailRoutesCache(mailRoutes)
      dispatch(accessControlLoaded({ permissions, mailRoutes }))
    },
    [dispatch]
  )

  /** RTDB'den yükle; boşsa seed yaz */
  const loadAccessControl = useCallback(async () => {
    dispatch(accessControlLoadStart())
    try {
      const db = getDb()
      const [permSnap, mailSnap] = await Promise.all([
        get(ref(db, `${ROOT}/permissions`)),
        get(ref(db, `${ROOT}/mailRoutes`)),
      ])

      let permissions = mapPermissionsFromRtdb(permSnap.val())
      let mailRoutes = normalizeMailRoutes(mailSnap.val())

      const needsPermSeed = !permSnap.exists() || Object.keys(permissions).length === 0
      const needsMailSeed = !mailSnap.exists()

      if (needsPermSeed || needsMailSeed) {
        const seedPerms = buildSeedPermissions()
        const seedMail = buildSeedMailRoutes()
        const multi = {}

        if (needsPermSeed) {
          Object.values(seedPerms).forEach((entry) => {
            const key = emailToKey(entry.email)
            multi[`${ROOT}/permissions/${key}`] = {
              email: entry.email,
              reports: entry.reports,
              updatedAt: new Date().toISOString(),
              updatedBy: 'seed',
            }
          })
          permissions = seedPerms
        }

        if (needsMailSeed) {
          multi[`${ROOT}/mailRoutes`] = seedMail
          mailRoutes = normalizeMailRoutes(seedMail)
        }

        if (Object.keys(multi).length) {
          await update(ref(db), multi)
        }
      }

      // Eski seed: klasik talepler To boştu → env varsayılanını doldur
      const defaultTo = String(process.env.REACT_APP_MAIL_TO_ADDRESS || '')
        .trim()
        .toLowerCase()
      if (defaultTo && mailSnap.exists()) {
        const classicKeys = ['tesekkur', 'sikayet', 'oneri-talep', 'default']
        let patched = false
        const nextMail = { ...mailRoutes }
        classicKeys.forEach((key) => {
          const route = nextMail[key] || { to: [], cc: [] }
          if (!Array.isArray(route.to) || route.to.length === 0) {
            nextMail[key] = { ...route, to: [defaultTo] }
            patched = true
          }
        })
        if (patched) {
          await set(ref(db, `${ROOT}/mailRoutes`), nextMail)
          mailRoutes = normalizeMailRoutes(nextMail)
        }
      }

      hydrateCaches(permissions, mailRoutes)
      return { permissions, mailRoutes }
    } catch (error) {
      console.log('loadAccessControl:', error)
      dispatch(accessControlLoadFail())
      // Fallback seed cache
      const seedPerms = buildSeedPermissions()
      const seedMail = normalizeMailRoutes(null)
      setPermissionsCache(seedPerms, false)
      setMailRoutesCache(seedMail)
      dispatch(
        accessControlLoaded({
          permissions: seedPerms,
          mailRoutes: seedMail,
        })
      )
      return null
    }
  }, [dispatch, hydrateCaches])

  const savePermission = useCallback(
    async (email, reports) => {
      const normalized = String(email || '').trim().toLowerCase()
      if (!normalized) {
        toastErrorNotify('E-posta zorunludur.')
        throw new Error('E-posta zorunludur.')
      }
      const key = emailToKey(normalized)
      const payload = {
        email: normalized,
        reports: { ...emptyReports(), ...reports },
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser || 'unknown',
      }
      try {
        const db = getDb()
        await set(ref(db, `${ROOT}/permissions/${key}`), payload)
        dispatch(setPermissionLocal(payload))
        const next = {
          ...accessState.permissions,
          [normalized]: payload,
        }
        setPermissionsCache(next, true)
        toastSuccessNotify('Yetkiler kaydedildi.')
        return payload
      } catch (error) {
        console.log('savePermission:', error)
        toastErrorNotify('Yetki kaydedilemedi.')
        throw error
      }
    },
    [accessState.permissions, currentUser, dispatch]
  )

  const saveMailRoutes = useCallback(
    async (mailRoutes) => {
      const normalized = normalizeMailRoutes(mailRoutes)
      try {
        const db = getDb()
        await set(ref(db, `${ROOT}/mailRoutes`), normalized)
        setMailRoutesCache(normalized)
        dispatch(setMailRoutesLocal(normalized))
        toastSuccessNotify('Mail yönlendirme kaydedildi.')
        return normalized
      } catch (error) {
        console.log('saveMailRoutes:', error)
        toastErrorNotify('Mail yönlendirme kaydedilemedi.')
        throw error
      }
    },
    [dispatch]
  )

  /** Senkron okuma — sendMail için; Redux veya seed */
  const getMailRouteForTopic = useCallback(
    (topic) => {
      const key = topicToMailRouteKey(topic)
      const routes = accessState.mailRoutes || {}
      const route = routes[key] || routes.default || { to: [], cc: [] }
      return {
        to: Array.isArray(route.to) ? route.to : [],
        cc: Array.isArray(route.cc) ? route.cc : [],
      }
    },
    [accessState.mailRoutes]
  )

  const listAuthUsers = useCallback(async () => {
    try {
      const fn = httpsCallable(getCallables(), 'listAuthUsers')
      const result = await fn({})
      return result?.data?.users || []
    } catch (error) {
      console.log('listAuthUsers:', error)
      toastErrorNotify(error?.message || 'Auth kullanıcıları alınamadı. Functions deploy edildi mi?')
      throw error
    }
  }, [])

  const createAuthUser = useCallback(async ({ email, password }) => {
    const e = String(email || '').trim().toLowerCase()
    const p = String(password || '')
    if (!e || !p) {
      toastErrorNotify('E-posta ve şifre zorunludur.')
      throw new Error('E-posta ve şifre zorunludur.')
    }
    if (p.length < 6) {
      toastErrorNotify('Şifre en az 6 karakter olmalıdır.')
      throw new Error('Şifre en az 6 karakter olmalıdır.')
    }
    try {
      const fn = httpsCallable(getCallables(), 'createAuthUser')
      const result = await fn({ email: e, password: p })
      toastSuccessNotify('Kullanıcı oluşturuldu.')
      return result?.data
    } catch (error) {
      console.log('createAuthUser:', error)
      toastErrorNotify(error?.message || 'Kullanıcı oluşturulamadı.')
      throw error
    }
  }, [])

  const setAuthUserDisabled = useCallback(async ({ uid, disabled }) => {
    if (!uid) throw new Error('uid zorunludur.')
    try {
      const fn = httpsCallable(getCallables(), 'setAuthUserDisabled')
      const result = await fn({ uid, disabled: Boolean(disabled) })
      toastSuccessNotify(disabled ? 'Kullanıcı pasifleştirildi.' : 'Kullanıcı aktifleştirildi.')
      return result?.data
    } catch (error) {
      console.log('setAuthUserDisabled:', error)
      toastErrorNotify(error?.message || 'Kullanıcı durumu güncellenemedi.')
      throw error
    }
  }, [])

  const reseedDefaults = useCallback(async () => {
    try {
      const db = getDb()
      const seedPerms = buildSeedPermissions()
      const seedMail = buildSeedMailRoutes()
      const multi = { [`${ROOT}/mailRoutes`]: seedMail }
      Object.values(seedPerms).forEach((entry) => {
        const key = emailToKey(entry.email)
        multi[`${ROOT}/permissions/${key}`] = {
          email: entry.email,
          reports: entry.reports,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUser || 'reseed',
        }
      })
      await update(ref(db), multi)
      hydrateCaches(seedPerms, normalizeMailRoutes(seedMail))
      toastSuccessNotify('Varsayılan yetkiler yüklendi.')
      return true
    } catch (error) {
      console.log('reseedDefaults:', error)
      toastErrorNotify('Varsayılanlar yüklenemedi.')
      throw error
    }
  }, [currentUser, hydrateCaches])

  return {
    ...accessState,
    loadAccessControl,
    savePermission,
    saveMailRoutes,
    getMailRouteForTopic,
    listAuthUsers,
    createAuthUser,
    setAuthUserDisabled,
    reseedDefaults,
  }
}

export default useAccessControl
